import React from 'react';

import { Button, Col, Form, Row } from 'antd';

import {
  CloseCircleOutlined,
  PlusOutlined,
  CopyOutlined,
} from '@ant-design/icons';

import {
  FormAddButton,
  LabelFormItem,
  dialogFunction,
} from 'common/components';

import {
  FormItemContainer,
  GroupElementWrapper,
  HeaderGroupElement,
} from '../../../shared/components';

import { FormPrimitiveItemOverlay } from './FormItemOverlayItem';
import { FormSingleNestedValueOverlay } from './FormSingleNestedValueOverlay';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../ProductDetailContext';

import { useCallbackRef } from 'hooks';

import {
  checkDisabledDeleteFormField,
  checkIsValidObjectValues,
  checkTypeOfFormNested,
  generateNestedIdxFormElement,
  getFieldValueFromListIdx,
  getFormNameItem,
  getIdxCopiedField,
} from '../utils';

import {
  checkHiddenFormField,
  generateOriginalNestedIdxFormElement,
  generateOriginalStringPath,
  reindexedFields,
} from './utils';

import { LIST_PROPERTY_NAME_NEED_TO_DISABLE_COPY_ACTION } from 'pages/product-full-view/constants';

export const FormMultipleNestedValueOverlay = ({
  properties,
  name,
  label,
  isArray,
  property,
  parentNestedIndex = [],
  productEnums = [],
  originalProductModules = {},
  ...otherProps
}) => {
  const {
    formInstance,
    overlay: { deletedFieldsOverlay },
  } = useProductDetail();

  const dispatch = useProductDetailDispatch();

  const { propertyName: parentPropertyName } = property;

  const renderItem = ({ property, indexField, parentNestedIndex }) => {
    const { propertyName, propertyDisplayName, isArray, childProperties } =
      property;

    const nameFormItem = getFormNameItem(indexField, propertyName);

    const { isSingleNestedValue, isMultipleNestedValue } =
      checkTypeOfFormNested(childProperties, isArray);

    if (isSingleNestedValue) {
      const singleParentNestedIndex = parentNestedIndex.concat(propertyName);

      return (
        <FormSingleNestedValueOverlay
          name={nameFormItem}
          property={property}
          productEnums={productEnums}
          parentNestedIndex={singleParentNestedIndex}
          originalProductModules={originalProductModules}
        />
      );
    }

    if (isMultipleNestedValue) {
      return (
        <FormMultipleNestedValueOverlay
          properties={childProperties}
          name={nameFormItem}
          isArray={isArray}
          label={propertyDisplayName}
          property={property}
          parentNestedIndex={parentNestedIndex}
          productEnums={productEnums}
          originalProductModules={originalProductModules}
        />
      );
    } else {
      return (
        <FormPrimitiveItemOverlay
          nameFormItem={nameFormItem}
          originalProductModules={originalProductModules}
          parentNestedIndex={parentNestedIndex}
          productEnums={productEnums}
          property={property}
        />
      );
    }
  };

  const handleUpdateDeletedFieldsOverlay = (indexesField) => {
    const originalFieldFormValues = getFieldValueFromListIdx(
      originalProductModules,
      indexesField
    );

    if (
      originalFieldFormValues === null ||
      originalFieldFormValues === undefined
    ) {
      return;
    }

    dispatch({
      type: productDetailActionTypes.UPDATE_DELETED_FIELDS_OVERLAY,
      pathField: generateOriginalStringPath(indexesField),
    });
  };

  const handleDeleteFieldForm = useCallbackRef(
    ({ idxFormElement, idxOriginalFormElement, field, removeCallback }) => {
      const formValues = formInstance.getFieldsValue();
      const fieldFormValues = getFieldValueFromListIdx(
        formValues,
        idxFormElement
      );
      const isValidValues = checkIsValidObjectValues(fieldFormValues);
      // Only show confirm modal when field has any data
      if (isValidValues) {
        dialogFunction({
          type: 'warn',
          content: 'Are you sure you want to delete selected property?',
          okText: 'Delete',
          okButtonProps: {
            type: 'danger',
          },
          onOk: () => {
            removeCallback(field.name);
            handleUpdateDeletedFieldsOverlay(idxOriginalFormElement);
          },
        });
      } else {
        removeCallback(field.name);
        handleUpdateDeletedFieldsOverlay(idxOriginalFormElement);
      }
    }
  );

  return (
    <Form.List name={name} {...otherProps}>
      {(fields, { add, remove }) => {
        const newFields = reindexedFields({
          fields,
          deletedFields: deletedFieldsOverlay,
          parentNestedIndex,
          propertyName: parentPropertyName,
        });

        return (
          <>
            {newFields.map((field, index) => {
              const concatenatedParentNestedIndex =
                generateNestedIdxFormElement({
                  parentNestedIndex,
                  propertyName: parentPropertyName,
                  field,
                });

              const isHidden = checkHiddenFormField({
                parentNestedIndex,
                field,
                propertyName: property.propertyName,
                deletedFieldsOverlay,
              });

              if (isHidden) return null;

              return (
                <Row
                  style={{
                    marginTop: 20,
                    position: 'relative',
                    marginRight: 16,
                  }}
                  wrap={false}
                  key={field.name}
                >
                  <GroupElementWrapper
                    flex='auto'
                    style={{
                      marginLeft: 24,
                      marginBottom: 8,
                    }}
                    className='product-detail__group-element'
                  >
                    <HeaderGroupElement
                      header={`${label} ${index + 1}`}
                      className='product-detail__group-header'
                    />

                    {properties.map((property) => {
                      if (property?.childProperties?.length > 0) {
                        return (
                          <FormItemContainer
                            key={[field.name, property.propertyName]}
                            fieldKey={[field.fieldKey, property.propertyName]}
                          >
                            {renderItem({
                              property,
                              indexField: [field.name],
                              parentNestedIndex: concatenatedParentNestedIndex,
                            })}
                          </FormItemContainer>
                        );
                      }

                      return (
                        <React.Fragment
                          key={[field.name, property.propertyName]}
                        >
                          {renderItem({
                            property,
                            indexField: [field.name],
                            parentNestedIndex: concatenatedParentNestedIndex,
                          })}
                        </React.Fragment>
                      );
                    })}
                  </GroupElementWrapper>

                  {fields.length > 0 ? (
                    <Col
                      flex='0 0 auto'
                      style={{
                        marginLeft: 4,
                        marginRight: 4,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Button
                        type='danger'
                        onClick={() => {
                          handleDeleteFieldForm({
                            idxFormElement: concatenatedParentNestedIndex,
                            idxOriginalFormElement:
                              generateOriginalNestedIdxFormElement({
                                parentNestedIndex,
                                propertyName: parentPropertyName,
                                field,
                              }),
                            field,
                            removeCallback: remove,
                          });
                        }}
                        icon={<CloseCircleOutlined />}
                        className='product-detail__btn-delete'
                        disabled={checkDisabledDeleteFormField({
                          totalFields: fields.length,
                          fieldFullPath: property.fieldFullPath,
                        })}
                      />

                      <Button
                        type='primary'
                        className='product-detail__btn-copy'
                        icon={<CopyOutlined />}
                        disabled={LIST_PROPERTY_NAME_NEED_TO_DISABLE_COPY_ACTION.includes(
                          property.propertyName
                        )}
                        onClick={() => {
                          const idxCopyField = getIdxCopiedField({
                            parentNestedIndex,
                            parentPropertyName,
                            field,
                          });

                          dispatch({
                            type: productDetailActionTypes.COPY_FIELD_FORM_OVERLAY,
                            payload: {
                              formInstance,
                              idxCopyField,
                            },
                          });
                        }}
                      />
                    </Col>
                  ) : null}
                </Row>
              );
            })}

            <Row className='product-detail__multiple-form-container'>
              <Col
                flex='0 0 300px'
                style={{
                  padding: '4px 0px',
                  textAlign: 'right',
                }}
                className='product-detail__label-form'
              >
                <LabelFormItem
                  label={property.propertyDisplayName}
                  tooltip={property.propertyDescription}
                  fieldFullPath={property.fieldFullPath}
                />
              </Col>

              <FormAddButton
                type='secondary'
                onClick={() => add()}
                icon={<PlusOutlined />}
                disabled={isArray ? false : fields.length > 0}
                text='Add value'
              />
            </Row>
          </>
        );
      }}
    </Form.List>
  );
};
